import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { LevelUp as IconLevelUp } from '@/app/quanta/components/icons/LevelUp';
import { Button } from '@/components/button';
import QuantaIcon from '@/components/QuantaIcon';
import { cn } from '@/utils/cn';

import PointsProgress from './PointsProgress';

interface ProgressCardProps {
  isLevelUp: boolean;
  status: string;
  level: number;
  totalPoints: number;
  maxPoints: number;
}

const ProgressCard: React.FC<ProgressCardProps> = ({ isLevelUp, status, level, totalPoints, maxPoints }: ProgressCardProps) => {
  const t = useTranslations('quanta.progress');
  const tStatus = useTranslations('quanta.status');
  const tCommon = useTranslations('quanta.common');

  return (
    <div className="hidden xl:flex flex-col w-fit h-full bg-quanta-panel rounded-2xl border-2 border-border-primary">
      <div className="flex flex-1 items-center gap-6 py-4 px-8">
        <div className={cn('hidden 2xl:flex flex-col gap-1', isLevelUp ? 'xl:flex' : 'hidden')}>
          <p className="text-xl text-text-secondary leading-none">{t('currentStatus')}</p>
          <p className="text-xl font-medium text-text-primary leading-none">{status}</p>
        </div>
        <div className={cn('hidden 2xl:flex flex-col gap-1', isLevelUp ? 'xl:flex' : 'hidden')}>
          <p className="text-xl text-text-secondary leading-none">{t('level')}</p>
          <p className="text-xl font-medium text-text-primary leading-none">{level}</p>
        </div>
        {isLevelUp ? (
          <Link href="/quanta">
            <Button
              variant="primary"
              trailingIcon={
                <div className="hidden 1320:block">
                  <IconLevelUp />
                </div>
              }
            >
              {tStatus('levelUp')}
            </Button>
          </Link>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-10 w-[300px]">
              <p className="text-xl text-text-secondary leading-none">{t('progress')}</p>
              <div className="flex items-center gap-1">
                <span className="font-medium text-text-primary leading-[20px]">
                  {totalPoints} / {maxPoints}
                </span>
                <QuantaIcon alt={tCommon('quantaPointsAlt')} size={20} />
              </div>
            </div>
            <div className="flex items-center w-full py-px select-none">
              <PointsProgress totalPoints={totalPoints} maxPoints={maxPoints} label={t('progress')} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressCard;
